import { useEffect, useMemo, useState } from 'react';
import {
  useSalaryAdvances,
  useCreateSalaryAdvance,
  useUpdateSalaryAdvance,
  useApproveSalaryAdvance,
  useDeleteSalaryAdvance,
  useSalaryAdvanceRecoveries,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { HrModal } from './modals/HrModal';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { EmployeeSearchSelect } from '../../components/ui/EmployeeSearchSelect';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { DatePicker } from '../../components/ui/DatePicker';
import { employeeDisplayName, employeeInitials } from '../../utils/employee';
import { formatDateLabel } from '../../utils/dateFormat';
import type {
  HrEmployee,
  SalaryAdvance,
  SalaryAdvanceRecoveryMethod,
  SalaryAdvanceStatus,
} from '../../services/httpServices/hrService';
import {
  HandCoins,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Search,
  Clock,
  CheckCircle2,
  Ban,
  History,
  UserCheck,
  Wallet,
  CalendarDays,
} from 'lucide-react';

const PAGE_LIMIT = 20;
const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
const labelClass = 'block text-sm font-medium text-text mb-1';

const currency = (value?: number | string) =>
  `৳${Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const ADVANCE_STATUS: Record<SalaryAdvanceStatus, { label: string; className: string; icon: React.ElementType }> = {
  PENDING: { label: 'Pending', className: 'bg-warning/10 text-warning', icon: Clock },
  APPROVED: { label: 'Approved', className: 'bg-primary/10 text-primary', icon: CheckCircle2 },
  ACTIVE: { label: 'Active', className: 'bg-success/10 text-success', icon: UserCheck },
  CLOSED: { label: 'Closed', className: 'bg-text-muted/10 text-text-muted', icon: CheckCircle2 },
  CANCELLED: { label: 'Cancelled', className: 'bg-danger/10 text-danger', icon: Ban },
};

const RECOVERY_METHOD_LABEL: Record<SalaryAdvanceRecoveryMethod, string> = {
  ONE_TIME: 'One-time',
  INSTALLMENT: 'Installment',
};

interface AdvanceFormState {
  employeeId: string;
  amount: string;
  issuedDate: string;
  reason: string;
  recoveryMethod: SalaryAdvanceRecoveryMethod;
  installmentAmount: string;
  installments: string;
  notes: string;
}

const emptyForm = (): AdvanceFormState => ({
  employeeId: '',
  amount: '',
  issuedDate: todayISO(),
  reason: '',
  recoveryMethod: 'INSTALLMENT',
  installmentAmount: '',
  installments: '',
  notes: '',
});

function AdvanceStatusPill({ status }: { status: SalaryAdvanceStatus }) {
  const meta = ADVANCE_STATUS[status] ?? { label: status, className: 'bg-text-muted/10 text-text-muted', icon: Clock };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${meta.className}`}>
      <Icon size={12} />
      {meta.label}
    </span>
  );
}

export function SalaryAdvances() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [status, setStatus] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SalaryAdvance | null>(null);
  const [form, setForm] = useState<AdvanceFormState>(emptyForm());
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<SalaryAdvance | null>(null);
  const [historyTarget, setHistoryTarget] = useState<SalaryAdvance | null>(null);

  const { data, isLoading, error } = useSalaryAdvances({
    page,
    limit: PAGE_LIMIT,
    search: debouncedSearch || undefined,
    status: status || undefined,
  });
  const createAdvance = useCreateSalaryAdvance();
  const updateAdvance = useUpdateSalaryAdvance();
  const approveAdvance = useApproveSalaryAdvance();
  const deleteAdvance = useDeleteSalaryAdvance();

  const advances = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
    setFormOpen(false);
    setFormError('');
  };

  const startCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setFormError('');
    setFormOpen(true);
  };

  const startEdit = (advance: SalaryAdvance) => {
    setEditing(advance);
    setForm({
      employeeId: advance.employeeId,
      amount: String(advance.amount ?? ''),
      issuedDate: String(advance.issuedDate ?? '').slice(0, 10),
      reason: advance.reason || '',
      recoveryMethod: advance.recoveryMethod,
      installmentAmount: advance.installmentAmount != null ? String(advance.installmentAmount) : '',
      installments: advance.installments != null ? String(advance.installments) : '',
      notes: advance.notes || '',
    });
    setFormError('');
    setFormOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!editing && !form.employeeId) {
      setFormError('Please select an employee.');
      return;
    }
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setFormError('Enter a valid advance amount greater than zero.');
      return;
    }
    if (form.recoveryMethod === 'INSTALLMENT') {
      const installment = Number(form.installmentAmount);
      if (!Number.isFinite(installment) || installment <= 0) {
        setFormError('An installment amount is required for installment recovery.');
        return;
      }
    }

    const base = {
      amount,
      issuedDate: form.issuedDate,
      reason: form.reason || undefined,
      recoveryMethod: form.recoveryMethod,
      installmentAmount: form.recoveryMethod === 'INSTALLMENT' ? Number(form.installmentAmount) : undefined,
      installments: form.installments ? Number(form.installments) : undefined,
      notes: form.notes || undefined,
    };

    if (editing) {
      updateAdvance.mutate(
        { id: editing.id, data: base },
        { onSuccess: resetForm },
      );
    } else {
      createAdvance.mutate({ employeeId: form.employeeId, ...base }, { onSuccess: resetForm });
    }
  };

  const handleApprove = (advance: SalaryAdvance) => {
    approveAdvance.mutate(advance.id);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdvance.mutateAsync(deleteTarget.id);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setDeleteTarget(null);
    }
  };

  const submitting = createAdvance.isPending || updateAdvance.isPending;
  const hasActiveFilters = Boolean(search || status);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <HandCoins className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Salary Advances</h1>
            <p className="text-sm text-text-muted">
              Advances and loans recovered automatically from each pay run.
            </p>
          </div>
        </div>
        <button
          onClick={startCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          New Advance
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load salary advances</span>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by employee name or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="ACTIVE">Active</option>
            <option value="CLOSED">Closed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setSearch('');
                setStatus('');
              }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Employee</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Outstanding</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Recovery</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Issued</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {advances.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                      {hasActiveFilters ? (
                        <span>
                          No advances match your filters.{' '}
                          <button
                            onClick={() => {
                              setSearch('');
                              setStatus('');
                            }}
                            className="text-primary hover:underline"
                          >
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No salary advances yet. Add one to start recovering from payroll.'
                      )}
                    </td>
                  </tr>
                ) : (
                  advances.map((advance) => {
                    const employee = advance.employee as HrEmployee | null | undefined;
                    const outstanding = Number(advance.outstandingBalance) || 0;
                    return (
                      <tr key={advance.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold overflow-hidden">
                              {employee?.photoUrl ? (
                                <img src={employee.photoUrl} alt="" className="w-full h-full object-cover" />
                              ) : employee ? (
                                employeeInitials(employee)
                              ) : (
                                <Wallet size={14} />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-text truncate">
                                {employee ? employeeDisplayName(employee) : 'Employee'}
                              </p>
                              <p className="text-xs text-text-muted truncate">
                                {[employee?.employeeCode, employee?.department?.name].filter(Boolean).join(' · ') || '—'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-text whitespace-nowrap">
                          {currency(advance.amount)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                          <span className={outstanding > 0 ? 'text-text font-medium' : 'text-text-muted'}>
                            {currency(outstanding)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted">
                          {RECOVERY_METHOD_LABEL[advance.recoveryMethod] ?? advance.recoveryMethod}
                          {advance.recoveryMethod === 'INSTALLMENT' && advance.installmentAmount != null && (
                            <span className="block text-xs text-text-muted">
                              {currency(advance.installmentAmount)} / period
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted whitespace-nowrap">
                          {formatDateLabel(advance.issuedDate)}
                        </td>
                        <td className="px-4 py-3">
                          <AdvanceStatusPill status={advance.status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {advance.status === 'PENDING' && (
                              <button
                                onClick={() => handleApprove(advance)}
                                disabled={approveAdvance.isPending}
                                className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-success transition-colors disabled:opacity-50"
                                title="Approve"
                              >
                                <UserCheck size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => setHistoryTarget(advance)}
                              className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors"
                              title="Recovery history"
                            >
                              <History size={16} />
                            </button>
                            <button
                              onClick={() => startEdit(advance)}
                              className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(advance)}
                              className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={PAGE_LIMIT}
            onPageChange={setPage}
            itemName="advances"
          />
        </div>
      )}

      {formOpen && (
        <HrModal
          open
          onClose={resetForm}
          title={editing ? 'Edit Salary Advance' : 'New Salary Advance'}
          subtitle="Recovered automatically from payroll once approved"
          icon={HandCoins}
          maxWidth="max-w-xl"
          footer={
            <>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="advance-form"
                disabled={submitting}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {editing ? 'Save Changes' : 'Create Advance'}
              </button>
            </>
          }
        >
          <form id="advance-form" onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="bg-danger/10 text-danger rounded-lg p-3 text-sm font-medium border border-danger/20">
                {formError}
              </div>
            )}

            {editing ? (
              <div>
                <label className={labelClass}>Employee</label>
                <div className="px-3 py-2 border border-border rounded-lg bg-background/60 text-sm text-text">
                  {editing.employee
                    ? employeeDisplayName(editing.employee as HrEmployee)
                    : 'Selected employee'}
                </div>
              </div>
            ) : (
              <EmployeeSearchSelect
                value={form.employeeId}
                onChange={(employeeId) => setForm((prev) => ({ ...prev, employeeId }))}
                label="Employee"
                required
                showPreview
              />
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Amount <span className="text-danger">*</span>
                </label>
                <ValidatedNumberInput
                  value={form.amount}
                  onChange={(v) => setForm({ ...form, amount: String(v) })}
                  placeholder="e.g. 10000"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Issued Date</label>
                <DatePicker
                  value={form.issuedDate}
                  onChange={(value) => setForm({ ...form, issuedDate: value })}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Recovery Method</label>
              <div className="grid grid-cols-2 gap-3">
                {(['INSTALLMENT', 'ONE_TIME'] as SalaryAdvanceRecoveryMethod[]).map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setForm({ ...form, recoveryMethod: method })}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      form.recoveryMethod === method
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-text-muted hover:text-text'
                    }`}
                  >
                    {RECOVERY_METHOD_LABEL[method]}
                  </button>
                ))}
              </div>
            </div>

            {form.recoveryMethod === 'INSTALLMENT' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Installment Amount <span className="text-danger">*</span>
                  </label>
                  <ValidatedNumberInput
                    value={form.installmentAmount}
                    onChange={(v) => setForm({ ...form, installmentAmount: String(v) })}
                    placeholder="e.g. 2000"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Installments</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.installments}
                    onChange={(e) => setForm({ ...form, installments: e.target.value })}
                    placeholder="Optional"
                    className={inputClass}
                  />
                </div>
              </div>
            )}

            <div>
              <label className={labelClass}>Reason</label>
              <input
                type="text"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Medical emergency"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Optional notes"
                className={inputClass}
              />
            </div>
          </form>
        </HrModal>
      )}

      {historyTarget && (
        <RecoveryHistoryModal advance={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete salary advance?"
        description="This removes the advance record. Any recovery already taken in a paid run is reversed."
        confirmLabel="Delete advance"
        loading={deleteAdvance.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function RecoveryHistoryModal({ advance, onClose }: { advance: SalaryAdvance; onClose: () => void }) {
  const { data, isLoading } = useSalaryAdvanceRecoveries(advance.id);
  const recoveries = data?.data || [];

  return (
    <HrModal
      open
      onClose={onClose}
      title="Recovery History"
      subtitle={advance.employee ? employeeDisplayName(advance.employee as HrEmployee) : 'Salary advance'}
      icon={History}
      maxWidth="max-w-lg"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
        >
          Done
        </button>
      }
    >
      <div className="rounded-lg border border-border bg-background/60 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <CalendarDays size={14} />
          Issued {formatDateLabel(advance.issuedDate)}
        </div>
        <div className="text-sm">
          <span className="text-text-muted">Outstanding </span>
          <span className="font-semibold text-text">{currency(advance.outstandingBalance)}</span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : recoveries.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-muted">
          No recoveries yet. An installment is recorded when a payroll containing this advance is marked PAID.
        </p>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
          {recoveries.map((recovery) => (
            <div key={recovery.id} className="flex items-center justify-between px-3 py-2.5">
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <CheckCircle2 size={14} className="text-success" />
                {formatDateLabel(recovery.recoveredAt)}
              </div>
              <span className="text-sm font-medium text-text">{currency(recovery.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </HrModal>
  );
}
